import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/synapse/Icon';
import { Logo } from '@/components/synapse/Logo';
import { LanguageToggle } from '@/components/synapse/LanguageToggle';
import { StatusDot } from '@/components/synapse/StatusDot';
import {
  usePatientPortal,
  usePortalReportUrl,
} from '@/features/patient-portal/usePatientPortal';
import { translateApiError } from '@/lib/apiError';
import { orderStatusTone, translateOrderStatus, translateSpecimenType } from '@/lib/labTests';
import type { PortalLabOrder, PortalLabTest } from '@/types/patientPortal';

/**
 * The patient's own view of their record, reached by a link a staff member gave
 * them. No session, no app shell, no navigation — the same shape as the other
 * `/p/*` pages.
 *
 * Whoever holds the link is treated as the patient: there is no authentication
 * yet, and adding it is the next phase. Two consequences are visible in the
 * code below and worth stating rather than discovering.
 *
 *  - The server decides what this page may know. Everything rendered here comes
 *    from a payload the API already narrowed; there is no filtering in this
 *    component, because a client-side filter over a fuller payload would still
 *    have shipped the fuller payload.
 *  - Report URLs are minted per tap and opened in a new tab. They expire in
 *    about two minutes, so nothing is prefetched.
 *
 * Labs are the only section today. `PortalSection` exists so the second one
 * (prescriptions, visits) is an addition rather than a restructure.
 */
export function PatientPortalPage() {
  const { t } = useTranslation();
  const { portalToken } = useParams<{ portalToken: string }>();
  const portal = usePatientPortal(portalToken);

  if (portal.isPending) {
    return (
      <Shell>
        <p className="text-[13.5px] text-muted">{t('patientPortal.loading')}</p>
      </Shell>
    );
  }

  // A bad, mistyped or retired link — including one whose patient record was
  // removed, since the lookup filters those out. Deliberately a dead end: there
  // is nowhere useful to send someone who is not a staff member, and offering a
  // sign-in would be an invitation to try credentials that do not exist yet.
  if (portal.isError || !portal.data) {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-paper-2 text-muted">
            <Icon name="alert" size={22} />
          </div>
          <h1 className="text-lg font-semibold tracking-[-0.02em]">
            {t('patientPortal.notFoundTitle')}
          </h1>
          <p className="max-w-[340px] text-[13.5px] leading-relaxed text-muted">
            {t('patientPortal.notFoundBody')}
          </p>
        </div>
      </Shell>
    );
  }

  const { patient, clinic, labs } = portal.data;

  return (
    <Shell clinicName={clinic.name}>
      <header className="mb-5">
        <h1 className="text-xl font-semibold tracking-[-0.02em] sm:text-2xl">
          {patient.fullName}
        </h1>
        <p className="mt-1 font-mono text-[12.5px] tabular-nums text-muted">
          {t('patientPortal.mrnLabel')} {patient.mrn}
        </p>
      </header>

      <PortalSection title={t('patientPortal.labsTitle')}>
        {labs.length === 0 ? (
          <p className="px-3.5 py-6 text-center text-[13px] text-muted">
            {t('patientPortal.noLabs')}
          </p>
        ) : (
          <ul className="flex flex-col">
            {labs.map((order) => (
              <li key={order.id} className="border-t border-line-2 first:border-t-0">
                <OrderCard order={order} portalToken={portalToken} />
              </li>
            ))}
          </ul>
        )}
      </PortalSection>

      {/* Said once, at the bottom, rather than beside every result. A page of
          numbers with no clinician attached invites self-diagnosis, and this is
          the only thing on it that pushes back. */}
      <p className="mt-5 flex items-start gap-2 rounded-md border border-line bg-paper-2 px-3.5 py-3 text-[12.5px] leading-snug text-muted">
        <Icon name="info" size={16} className="mt-px shrink-0 text-info" />
        {t('patientPortal.clinicalNote')}
      </p>
    </Shell>
  );
}

function PortalSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="overflow-hidden rounded-lg border border-line bg-paper">
      <h2 className="border-b border-line-2 bg-paper-2 px-3.5 py-2.5 text-[12.5px] font-semibold uppercase tracking-[0.04em] text-muted">
        {title}
      </h2>
      {children}
    </section>
  );
}

const TONE_TO_DOT = {
  success: 'ok',
  info: 'warn',
  danger: 'danger',
  neutral: 'neutral',
  muted: 'neutral',
} as const;

function OrderCard({
  order,
  portalToken,
}: {
  order: PortalLabOrder;
  portalToken: string | undefined;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2.5 px-3.5 py-3">
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        <span className="font-mono text-[12.5px] tabular-nums text-ink">
          {order.accessionNumber}
        </span>
        <span className="text-[12px] text-muted">
          {t('patientPortal.orderedOn')} {order.orderedOn.toLocaleDateString()}
        </span>
        <span className="inline-flex items-center gap-1.5 text-[12px] text-muted">
          {/* The same tone map and the same translated status the lab desk
              sees, so a result never reads one way for staff and another for
              the person waiting on it. */}
          <StatusDot tone={TONE_TO_DOT[orderStatusTone(order.status)]} />
          {translateOrderStatus(t, order.status)}
        </span>
      </div>

      <p className="text-[12px] text-muted-2">
        {order.collectedAt
          ? `${t('patientPortal.collectedOn')} ${order.collectedAt.toLocaleDateString()}`
          : t('patientPortal.notCollectedYet')}
      </p>

      <ul className="flex flex-col gap-1.5">
        {order.tests.map((test) => (
          <li key={`${order.id}-${test.name}`}>
            <TestLine test={test} portalToken={portalToken} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function TestLine({
  test,
  portalToken,
}: {
  test: PortalLabTest;
  portalToken: string | undefined;
}) {
  const { t } = useTranslation();
  const mint = usePortalReportUrl(portalToken);
  const [error, setError] = useState<string | null>(null);

  async function open() {
    if (!test.documentId) return;
    setError(null);
    try {
      const { url } = await mint.mutateAsync(test.documentId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(translateApiError(err, { fallback: t('patientPortal.reportFailed') }));
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="text-[13px] text-ink-2">{test.name}</span>
      <span className="rounded-full border border-line-2 px-2 py-0.5 text-[11px] text-muted-2">
        {translateSpecimenType(t, test.specimenType)}
      </span>

      {test.documentId ? (
        <button
          type="button"
          onClick={() => void open()}
          disabled={mint.isPending}
          aria-label={t('patientPortal.reportFor', { test: test.name })}
          className="inline-flex min-h-[32px] items-center gap-1 text-[12px] text-ink underline underline-offset-2 disabled:opacity-50"
        >
          <Icon name="arrow_up_right" size={12} />
          {mint.isPending ? t('patientPortal.reportOpening') : t('patientPortal.viewReport')}
        </button>
      ) : (
        <span className="text-[11.5px] text-muted-2">{t('patientPortal.awaitingResult')}</span>
      )}

      {error && (
        <span role="alert" className="text-[11.5px] text-destructive">
          {error}
        </span>
      )}
    </div>
  );
}

/**
 * Full-screen chrome for the public page. Mirrors PublicRegisterPage: no
 * AppShell, no nav, nothing that hints at a staff area. The language toggle is
 * present because the person reading this is a patient choosing their own
 * language, not a staff member with a saved preference.
 */
function Shell({
  clinicName,
  children,
}: {
  clinicName?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-background px-4 py-6">
      <div className="mx-auto w-full max-w-[560px]">
        <div className="mb-6 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <Logo size={26} withWord={!clinicName} />
            {clinicName && (
              <span className="truncate text-[13.5px] font-medium text-ink-2">{clinicName}</span>
            )}
          </div>
          <LanguageToggle />
        </div>
        {children}
      </div>
    </div>
  );
}
